/**
 * net/mediaCache.ts — mídia e fila em disco (IndexedDB), com TETO.
 *
 * É isto que faz os critérios 6 (30 min sem Wi-Fi), 7 (expira às 22:00) e 8
 * (reboot sem rede mostra do cache) existirem. Sem cache, "continuidade offline"
 * é uma frase de apresentação.
 *
 * ⚠️ A REGRA DURA (PLAYER §4.5 / D5): **mídia da fila atual NUNCA é liberada.**
 * O oposto seria o player travar no meio do loop porque uma limpeza de disco
 * rodou junto. A liberação só acontece DEPOIS de a fila nova ser aplicada, e só
 * para o que a fila nova não usa (§4.3).
 *
 * ⚠️ O TETO é real e observável: `navigator.storage.estimate()` quando existe,
 * senão o default de 500 MB (D5). Estourar o teto NÃO é exceção fatal — a regra é
 * parar de aceitar download novo, continuar tocando o que já está, e registrar
 * o que ficou de fora. Estourar quota do IndexedDB mata o player.
 * ponytail: IndexedDB cru, sem biblioteca (`idb` seria +1 dep para 4 operações).
 * O envelope de falha do `IDBRequest` é o ponto chato e está em `request()`.
 */
import type { ID, MediaAsset } from '../src/domain/types';

/** D5: teto default do web quando o navegador não informa a cota. */
export const DEFAULT_CACHE_QUOTA_BYTES = 500 * 1024 * 1024;

/** Fila em cache + metadados. */
export interface CachedQueue {
  screenId: ID;
  version: string;
  generatedAt: string;
  serverTime: string;
  rotationEpochSeconds: number;
  items: CachedQueueItem[];
  /** Envelope cru (string) do payload: é o que `isQueueVersionValid` revalida. */
  rawPayload: string;
  /** Quando foi gravado (ms local) — base do aviso de "desatualizada" (D4). */
  cachedAt: number;
}

export interface CachedQueueItem {
  adId: ID;
  mediaId: ID;
  kind: 'image' | 'video';
  url: string;
  thumbnailUrl?: string;
  durationSeconds: number;
  sizeBytes?: number;
}

export interface CachedMediaRecord {
  mediaId: ID;
  sizeBytes: number;
  contentType: string;
  /** Blob pronto para `URL.createObjectURL` — sem rede. */
  blob: Blob;
  /** Último toque: base do LRU quando o teto aperta. */
  lastUsedAt: number;
  createdAt: number;
}

export type CacheOutcome =
  | { ok: true; bytes: number }
  | { ok: false; reason: 'quota' | 'too-big' | 'error'; detail?: string };

/**
 * Resultado do pré-download (§4.5). O receptor usa isto para decidir o que entra
 * no loop: item sem mídia disponível é PULADO, nunca "espera o download"
 * (PLAYER §4.5.3).
 */
export interface PrefetchReport {
  /** Mídia que ficou disponível. */
  ready: ID[];
  /** Mídia que não coube no teto (e por isso NÃO entra no loop). */
  skippedForQuota: ID[];
  /** Mídia que falhou ao baixar. */
  failed: ID[];
}

/** Store de IndexedDB — injetável para teste em Node (que não tem IDB). */
export interface CacheBackend {
  getQueue(screenId: ID): Promise<CachedQueue | null>;
  putQueue(queue: CachedQueue): Promise<void>;
  getMedia(mediaId: ID): Promise<CachedMediaRecord | null>;
  putMedia(record: CachedMediaRecord): Promise<void>;
  deleteMedia(mediaId: ID): Promise<void>;
  listMedia(): Promise<CachedMediaRecord[]>;
  clear(): Promise<void>;
  usage(): Promise<{ usage: number; quota: number } | null>;
}

/** IndexedDB real do browser. Ausente (SSR/Node) ⇒ o receptor segue sem cache. */
export function indexedDbBackend(dbName = 'simpleindor-receptor'): CacheBackend | null {
  if (typeof indexedDB === 'undefined') {
    return null;
  }
  const open = (): Promise<IDBDatabase> =>
    new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('queue')) {
          db.createObjectStore('queue');
        }
        if (!db.objectStoreNames.contains('media')) {
          db.createObjectStore('media', { keyPath: 'mediaId' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB indisponível'));
    });

  const run = async <T>(
    store: string,
    mode: IDBTransactionMode,
    work: (s: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> => {
    const db = await open();
    try {
      return await new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const request = work(tx.objectStore(store));
        // Request success is not durability: a later transaction abort must reject.
        tx.oncomplete = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('falha no IndexedDB'));
        tx.onabort = () => reject(tx.error ?? new Error('transação abortada'));
        tx.onerror = () => reject(tx.error ?? new Error('transação falhou'));
      });
    } finally {
      db.close();
    }
  };

  return {
    getQueue: screenId => run<CachedQueue | undefined>('queue', 'readonly', s => s.get(screenId)).then(v => v ?? null),
    putQueue: async queue => {
      await run('queue', 'readwrite', s => s.put(queue, queue.screenId));
    },
    getMedia: mediaId => run<CachedMediaRecord | undefined>('media', 'readonly', s => s.get(mediaId)).then(v => v ?? null),
    putMedia: async record => {
      await run('media', 'readwrite', s => s.put(record));
    },
    deleteMedia: async mediaId => {
      await run('media', 'readwrite', s => s.delete(mediaId));
    },
    listMedia: () => run<CachedMediaRecord[]>('media', 'readonly', s => s.getAll()),
    clear: async () => {
      await run('queue', 'readwrite', s => s.clear());
      await run('media', 'readwrite', s => s.clear());
    },
    usage: async () => {
      // D5: o navegador é a ÚNICA fonte de verdade da cota real.
      if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
        return null;
      }
      const estimate = await navigator.storage.estimate();
      return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0 };
    },
  };
}

/** Cache em memória — o que o Node usa nos testes e o plano B sem IndexedDB. */
export function memoryBackend(): CacheBackend {
  const queues = new Map<string, CachedQueue>();
  const media = new Map<string, CachedMediaRecord>();
  let usage = 0;
  return {
    getQueue: async screenId => queues.get(screenId) ?? null,
    putQueue: async queue => void queues.set(queue.screenId, queue),
    getMedia: async mediaId => media.get(mediaId) ?? null,
    putMedia: async record => {
      media.set(record.mediaId, record);
      usage = [...media.values()].reduce((sum, m) => sum + m.sizeBytes, 0);
    },
    deleteMedia: async mediaId => {
      media.delete(mediaId);
      usage = [...media.values()].reduce((sum, m) => sum + m.sizeBytes, 0);
    },
    listMedia: async () => [...media.values()],
    clear: async () => {
      queues.clear();
      media.clear();
      usage = 0;
    },
    usage: async () => ({ usage, quota: 0 }),
  };
}

export interface MediaCacheOptions {
  backend: CacheBackend;
  /** Teto duro do cache (D5). Default 500 MB. */
  maxBytes?: number;
  now?: () => number;
  onLog?: (message: string, detail?: unknown) => void;
}

/** Fonte da mídia — o `HttpGateway`, injetado para não acoplar cache a rede. */
export interface MediaFetcher {
  fetchMediaBytes(media: MediaAsset): Promise<Blob>;
}

export class MediaCache {
  private readonly backend: CacheBackend;
  private readonly maxBytes: number;
  private readonly now: () => number;
  private readonly log: (message: string, detail?: unknown) => void;

  /** MediaIds da fila ATUAL. Regra dura: nunca liberados (§4.5). */
  private protectedIds = new Set<ID>();

  constructor(options: MediaCacheOptions) {
    this.backend = options.backend;
    this.maxBytes = options.maxBytes ?? DEFAULT_CACHE_QUOTA_BYTES;
    this.now = options.now ?? (() => Date.now());
    this.log = options.onLog ?? (() => {});
  }

  /** Teto efetivo — exposto para o relatório e para a tela de espera. */
  get quotaBytes(): number {
    return this.maxBytes;
  }

  /**
   * Teto REAL: o menor entre o que a gente declarou e a cota que o navegador
   * diz. Estourar a cota do navegador lança exceção e pode matar o player
   * (PLAYER §4.5) — por isso consultamos antes de escrever, e não depois.
   */
  async effectiveCeiling(): Promise<number> {
    const usage = await this.backend.usage().catch(() => null);
    if (usage && usage.quota > 0) {
      return Math.min(this.maxBytes, Math.floor(usage.quota * 0.8));
    }
    return this.maxBytes;
  }

  async usedBytes(): Promise<number> {
    const all = await this.backend.listMedia();
    return all.reduce((sum, m) => sum + m.sizeBytes, 0);
  }

  async readQueue(screenId: ID): Promise<CachedQueue | null> {
    return this.backend.getQueue(screenId);
  }

  /**
   * Grava a fila. O `rawPayload` viaja junto porque `isQueueVersionValid` recalcula
   * o hash a partir do CONTEÚDO — sem o envelope original, o receptor não consegue
   * provar que a fila em disco é íntegra.
   */
  async writeQueue(queue: CachedQueue): Promise<void> {
    this.protectedIds = new Set(queue.items.map(item => item.mediaId));
    await this.backend.putQueue(queue);
  }

  async readMedia(mediaId: ID): Promise<CachedMediaRecord | null> {
    return this.backend.getMedia(mediaId);
  }

  /** Marca a mídia como usada (atualiza o LRU) — chamado pelo player ao exibir. */
  async touch(mediaId: ID): Promise<void> {
    const record = await this.backend.getMedia(mediaId);
    if (!record) {
      return;
    }
    await this.backend.putMedia({ ...record, lastUsedAt: this.now() });
  }

  /**
   * Pré-download SEQUENCIAL (§4.5.4), na ordem que o chamador passou.
   *
   * A ordem é a prioridade: o receptor passa os itens começando pelo mais próximo
   * de ser exibido, então o primeiro que não couber é justamente o menos urgente.
   * Baixa um por um para não saturar o Wi-Fi de loja.
   *
   * O que NÃO cabe no teto é **pulado e reportado** — nunca derruba o download
   * inteiro nem marca como disponível.
   */
  async prefetch(
    items: { media: MediaAsset; priority: number }[],
    fetcher: MediaFetcher,
  ): Promise<PrefetchReport> {
    const report: PrefetchReport = { ready: [], skippedForQuota: [], failed: [] };
    // Prioridade MAIOR primeiro: o chamador passa "quanto mais perto, maior".
    const ordered = [...items].sort((a, b) => b.priority - a.priority);
    const ceiling = await this.effectiveCeiling();
    let used = await this.usedBytes();

    for (const item of ordered) {
      const asset = item.media;
      if (await this.backend.getMedia(asset.id)) {
        report.ready.push(asset.id);
        continue;
      }
      const size = typeof asset.sizeBytes === 'number' ? asset.sizeBytes : 0;
      if (used + size > ceiling) {
        // TETO: o item simplesmente não entra. O loop pula-o (PLAYER §4.5.3) e o
        // resto da programação continua — TV no chão seria pior.
        this.log('cache cheio — item fora do teto', { mediaId: asset.id, used, ceiling, size });
        report.skippedForQuota.push(asset.id);
        continue;
      }

      let blob: Blob;
      try {
        blob = await fetcher.fetchMediaBytes(asset);
      } catch (error) {
        // Falha de download: o item fica fora do loop, os outros continuam (§4.5.3).
        this.log('download falhou', { mediaId: asset.id, error: String(error) });
        report.failed.push(asset.id);
        continue;
      }

      const at = this.now();
      const stored: CacheOutcome = await this.storeMedia(asset, blob, at);
      if (stored.ok) {
        used += stored.bytes;
        report.ready.push(asset.id);
      } else {
        this.log('mídia recusada pelo cache', { mediaId: asset.id, reason: stored.reason });
        report.failed.push(asset.id);
      }
    }
    return report;
  }

  /** Grava uma mídia já baixada. O teto é verificado ANTES de escrever. */
  async storeMedia(asset: MediaAsset, blob: Blob, at: number = this.now()): Promise<CacheOutcome> {
    const ceiling = await this.effectiveCeiling();
    const used = await this.usedBytes();
    if (used + blob.size > ceiling) {
      return { ok: false, reason: 'quota', detail: `${used}+${blob.size} > ${ceiling}` };
    }
    try {
      await this.backend.putMedia({
        mediaId: asset.id,
        sizeBytes: blob.size,
        contentType: blob.type || (asset.kind === 'video' ? 'video/mp4' : 'application/octet-stream'),
        blob,
        lastUsedAt: at,
        createdAt: at,
      });
      return { ok: true, bytes: blob.size };
    } catch (error) {
      return { ok: false, reason: 'error', detail: String(error) };
    }
  }

  /**
   * Libera o que a fila nova NÃO usa (LRU). Só roda DEPOIS de aplicar a fila
   * (§4.3.4): enquanto o receptor pode estar offline rodando a programação
   * antiga, apagar arquivo é transformar um atraso em uma TV sem anúncio.
   *
   * `keepIds` = mídia da fila aplicada. Tudo fora disso e fora do protegido sai.
   */
  async evict(keepIds: ID[]): Promise<{ freedBytes: number; evicted: ID[] }> {
    const keep = new Set<ID>([...keepIds, ...this.protectedIds]);
    const all = await this.backend.listMedia();
    // LRU: o mais antigo menos usado primeiro. Empate resolve pelo id, para a
    // ordem ser determinística (o teste não pode passar por sorte).
    const doomed = all
      .filter(record => !keep.has(record.mediaId))
      .sort((a, b) => a.lastUsedAt - b.lastUsedAt || (a.mediaId < b.mediaId ? -1 : 1));

    let freedBytes = 0;
    const evicted: ID[] = [];
    for (const record of doomed) {
      await this.backend.deleteMedia(record.mediaId);
      freedBytes += record.sizeBytes;
      evicted.push(record.mediaId);
    }
    if (evicted.length > 0) {
      this.log('cache liberado', { evicted: evicted.length, freedBytes });
    }
    return { freedBytes, evicted };
  }

  /**
   * Encolhe até caber no teto, respeitando a regra dura. Chamado quando o teto
   * aperta (ex.: o navegador reduziu a cota): primeiro sai o que está fora da
   * fila, e só depois — se ainda não couber — a faixa de 7 dias entra.
   */
  async trimToFit(ceiling?: number): Promise<{ freedBytes: number }> {
    const limit = ceiling ?? (await this.effectiveCeiling());
    let used = await this.usedBytes();
    if (used <= limit) {
      return { freedBytes: 0 };
    }
    const all = await this.backend.listMedia();
    const keep = this.protectedIds;
    const candidates = all
      .filter(record => !keep.has(record.mediaId))
      .sort((a, b) => a.lastUsedAt - b.lastUsedAt || (a.mediaId < b.mediaId ? -1 : 1));
    let freedBytes = 0;
    for (const record of candidates) {
      if (used <= limit) {
        break;
      }
      await this.backend.deleteMedia(record.mediaId);
      used -= record.sizeBytes;
      freedBytes += record.sizeBytes;
    }
    return { freedBytes };
  }

  async clear(): Promise<void> {
    this.protectedIds = new Set();
    await this.backend.clear();
  }
}
